import { SvelteMap } from 'svelte/reactivity'
import { type FetchError, fetchUtils } from '$utils/fetchUtils'
import type { Tag } from '$types/tag'
import { addNotification } from '$lib/stores'
import { DefaultNotification, NotificationType } from '$types/notification'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'

/**
 * Shared, reactive source of truth for the tags of a lot, keyed by lotId.
 *
 * It is a module-level singleton: every component instance that reads
 * `lotTags.tags(lotId)` observes the same data, so a mutation triggered by one
 * instance (e.g. the editable tag list in the lot form) is reflected in every
 * other instance for that lot (e.g. the read-only list in the lot header).
 *
 * The module only depends on `fetchUtils` and the generic `Tag` type, so it can
 * be reused by any consumer dealing with lot tags.
 */
class LotTagsStore {
	readonly #tags = new SvelteMap<string, Tag[]>()
	readonly #options = new SvelteMap<string, Tag[]>()
	readonly #loadingTags = new Set<string>()
	readonly #loadingOptions = new Set<string>()

	/** Tags currently assigned to the lot. Reactive. */
	tags(lotId: string): Tag[] {
		return this.#tags.get(lotId) ?? []
	}

	/** Tags assignable to the lot (global or project level). Reactive. */
	options(lotId: string): Tag[] {
		return this.#options.get(lotId) ?? []
	}

	/**
	 * Load the tags assigned to the lot. Needed by every consumer (read-only and
	 * editable). Guarded against concurrent calls for the same lot.
	 */
	async loadTags(lotId: string): Promise<void> {
		if (!lotId || this.#loadingTags.has(lotId)) return
		this.#loadingTags.add(lotId)
		try {
			await this.#refreshTags(lotId)
		} catch (error) {
			console.error(error)
		} finally {
			this.#loadingTags.delete(lotId)
		}
	}

	/**
	 * Load the tags assignable to the lot. Only editable consumers need this: it
	 * feeds the autocomplete suggestions and lets `add` resolve a label to its
	 * publicId. Because the suggestions come from here, svelecte itself does not
	 * fetch — avoiding a duplicate request for the same endpoint.
	 */
	async loadOptions(lotId: string): Promise<void> {
		if (!lotId || this.#loadingOptions.has(lotId)) return
		this.#loadingOptions.add(lotId)
		try {
			await this.#refreshOptions(lotId)
		} catch (error) {
			console.error(error)
		} finally {
			this.#loadingOptions.delete(lotId)
		}
	}

	/** Assign a tag to the lot. Reuses an existing option when the label matches. */
	async add(lotId: string, label: string): Promise<void> {
		if (!lotId || !label) return
		// Do not add the same tag twice.
		if (this.tags(lotId).some((tag) => tag.label === label)) return
		// Reuse an existing tag (global or project level) when the label matches; the
		// backend creates a new project tag only when no tagPublicId is supplied.
		const existingTag = this.options(lotId).find((tag) => tag.label === label)
		const tagToAdd = existingTag ? { tagPublicId: existingTag.publicId } : { label }
		try {
			await fetchUtils.post(`/api/tags/lot/${lotId}`, tagToAdd, false)
		} catch (error) {
			const errorMessage = (error as FetchError).message
			addNotification(
				DefaultNotification(
					get(t)('UI.tags.notification.add.fail', { error: errorMessage }),
					NotificationType.ERROR
				)
			)
		} finally {
			// Adding may create a new project tag, so refresh the options too.
			await Promise.all([this.#refreshTags(lotId), this.#refreshOptions(lotId)])
		}
	}

	/** Remove a tag from the lot by its label. */
	async remove(lotId: string, label: string): Promise<void> {
		if (!lotId || !label) return
		const tagToRemove = this.tags(lotId).find((tag) => tag.label === label)
		if (!tagToRemove) return
		try {
			await fetchUtils.delete(`/api/tags/lot/${lotId}/${tagToRemove.publicId}`, false)
		} catch (error) {
			console.error(error)
			addNotification(
				DefaultNotification(
					get(t)('UI.tags.notification.remove.fail'),
					NotificationType.ERROR
				)
			)
		} finally {
			// Removing may remove the project tag, so refresh the options too.
			await Promise.all([this.#refreshTags(lotId), this.#refreshOptions(lotId)])
		}
	}

	async #refreshTags(lotId: string): Promise<void> {
		this.#tags.set(lotId, await fetchUtils.get(`/api/tags/lot/${lotId}`, false))
	}

	async #refreshOptions(lotId: string): Promise<void> {
		this.#options.set(lotId, await fetchUtils.get(`/api/tags/lot/${lotId}/options`, false))
	}
}

export const lotTags = new LotTagsStore()
